import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Modal,
  CircularProgress,
} from "@mui/material";
import { useReportGenerator } from "./useReportGenerator";
import CategorySelector from "./CategorySelector";
import DateRangeSelector from "./DateRangeSelector";
import ExportOptions from "./ExportOptions";
import ReportTable from "./ReportTable";
import { reportsApi } from "./reportsApi";
import Email from "../../images/managedata/email.svg";
import Print from "../../images/managedata/print.svg";
import EmailReportModal from "./EmailReportModal";
// import { Tabs, Tab } from "@mui/material";
import AnalyticsChart from "./Analytics/AnalyticsChart";

const ReportGenerator = ({
  reportType,
  vehicles = [],
  routes = [],
  drivers = [],
  stops = [],
  sites = [],
  users = [],
}) => {
  const [selectedCategories, setSelectedCategories] = useState({});
  const [dateOptions, setDateOptions] = useState({
    duration: "today",
    customDateRange: { startDate: "", endDate: "" },
  });
  const [openModal, setOpenModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("docx");
  const { generateReport, loading, error, reportData, clearError } =
    useReportGenerator();
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [allSites, setAllSites] = useState([]);
  const [settings, setSettings] = useState({});
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const setting = JSON.parse(localStorage.getItem("appSettings"));
    setSettings(setting);
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      setAllSites(sites);
      setSelectedSiteId(""); // Default to no site
    }
    // Debug stops
    const fetchStops = async () => {
      try {
        const stopsData = await reportsApi.getStopsByOrganization();
        console.log("Available Stops:", stopsData);
      } catch (err) {
        console.error("Error fetching stops:", err);
      }
    };
    fetchStops();
  }, [sites]);

  const handleSiteChange = (e) => {
    setSelectedSiteId(e.target.value);
  };

  const handleGenerateReport = async () => {
    try {
      await generateReport({
        reportType,
        selectedCategories,
        dateOptions,
        vehicles,
        routes,
        drivers,
        stops,
        users,
        siteId: selectedSiteId,
      });
      // setOpenModal(true);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const printDocument = printWindow.document;
    const tableContent = document.querySelector(
      // ".MuiModal-root .MuiTableContainer-root"
      ".MuiTableContainer-root"
    );
    if (!tableContent || !reportData) {
      alert("No content to print");
      return;
    }

    const printContent = `
      <html>
        <head>
          <title>${
            reportType.charAt(0).toUpperCase() + reportType.slice(1)
          } Report</title>
          <style>
            body { font-family: Roboto, Arial, sans-serif; margin: 20px; }
            h1 { color: #183059; text-align: center; }
            .filter-info { margin-bottom: 20px; }
            .filter-info p { font-size: 14px; color: #414958; }
            .filter-info span { color: #183059; font-weight: 500; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #B7BFCC; color: #333; font-weight: 600; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .zero-speed { color: red; }
            .no-schedules { color: gray; }
            @media print {
              body { margin: 0; }
              table { page-break-inside: auto; }
            }
          </style>
        </head>
        <body>
          <h1>${
            reportType.charAt(0).toUpperCase() + reportType.slice(1)
          } Report</h1>
          <div class="filter-info">
            <p>Date Range:<span>${getDateRange(dateOptions.duration)}</span></p>
            <p>
              Filter: <span>${
                (reportData && reportData.reportType) || "None"
              }</span>
            </p>
            <p>Selected Items: <span>${
              Object.entries(selectedCategories)
                .filter(
                  ([_, data]) =>
                    data.selectedItems.length > 0 || data.isAllSelected
                )
                .map(([key, data]) =>
                  data.isAllSelected
                    ? `All ${key}`
                    : data.selectedItems.length <= 2
                    ? data.selectedItems.join(", ")
                    : `${data.selectedItems.length} ${key}`
                )
                .join(", ") || "None"
            }</span></p>
            ${
              reportData.metrics
                ? `
              <p>Metrics: <span>
                Completed Trips: ${reportData.metrics.completedTrips},
                Delayed Trips: ${reportData.metrics.delayedTrips},
                Dead Status: ${reportData.metrics.deadStatusCount},
                Avg Speed: ${reportData.metrics.averageSpeed} km/h,
                Out of Route: ${reportData.metrics.outOfRouteEvents}
              </span></p>
            `
                : ""
            }
          </div>
          ${tableContent.outerHTML}
        </body>
      </html>
    `;

    printDocument.write(printContent);
    printDocument.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getDateRange = (duration) => {
    const today = new Date();
    const formatDate = (date) =>
      `${date.getDate()} ${date.toLocaleString("en-US", {
        month: "long",
      })} ${date.getFullYear()}`;
    let startDate, endDate;

    switch (duration) {
      case "today":
        startDate = endDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = startDate;
        break;
      case "last 7 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
        break;
      case "last 30 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        endDate = today;
        break;
      case "last 6 months":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 6);
        endDate = today;
        break;
      case "last 1 year":
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        endDate = today;
        break;
      case "custom":
        return dateOptions.customDateRange.startDate &&
          dateOptions.customDateRange.endDate
          ? `${formatDate(
              new Date(dateOptions.customDateRange.startDate)
            )} - ${formatDate(new Date(dateOptions.customDateRange.endDate))}`
          : "None";
      default:
        return "None";
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Enhanced function to add sortable and format properties to dynamic columns
  const enhanceColumns = (backendColumns) => {
    if (!backendColumns || !Array.isArray(backendColumns)) {
      return [];
    }

    return backendColumns.map((col) => {
      const enhanced = {
        key: col.key,
        label: col.label,
        sortable: true, // Default to sortable
      };

      // Add format based on column key patterns
      // if (col.key.toLowerCase().includes("speed")) {
      //   enhanced.format = "speed";
      // } else if (
      //   col.key.toLowerCase().includes("date") ||
      //   col.key.toLowerCase().includes("time")
      // ) {
      //   enhanced.format = "datetime";
      // } else if (col.key.toLowerCase().includes("duration")) {
      //   enhanced.format = "duration";
      //   enhanced.sortable = false;
      // } else if (col.key.toLowerCase().includes("location")) {
      //   enhanced.sortable = false;
      // }

      return enhanced;
    });
  };

  // Transform reportData for ReportTable - now using dynamic columns from backend
  const transformedReportData = reportData
    ? {
        ...reportData,
        // Use backend columns if available, otherwise fall back to default columns
        columns: reportData.columns
          ? enhanceColumns(reportData.columns)
          : [
              { key: "deviceId", label: "Device ID", sortable: true },
              { key: "vehicleNumber", label: "Vehicle Number", sortable: true },
              { key: "driverName", label: "Driver", sortable: true },
              { key: "routeName", label: "Route", sortable: true },
              { key: "siteName", label: "Site", sortable: true },
              {
                key: "organizationName",
                label: "Organization",
                sortable: true,
              },
              { key: "status", label: "Status", sortable: true },
              { key: "schedules", label: "Schedules", sortable: true },
              {
                key: "averageSpeed",
                label: "Avg Speed (km/h)",
                sortable: true,
                format: "speed",
              },
              { key: "outOfRouteCount", label: "Out of Route", sortable: true },
            ],
        data: reportData.data ? reportData.data : [],
      }
    : null;

  const getReportName = (type) => {
    if (type === "deviation") return "Route Deviation";
    if (type === "entryExit") {
      return "Stop Entry/Exit";
    }
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Add this helper function before the return statement
  const isAnyCategorySelected = Object.values(selectedCategories).some(
    (cat) =>
      cat.isAllSelected || (cat.selectedItems && cat.selectedItems.length > 0)
  );

  const handleEmailClick = () => {
    setEmailModalOpen(true);
  };

  const clearReportParamsFromStorage = () => {
    try {
      localStorage.removeItem("reportCategoryParams");
      console.log("Cleared report params from localStorage");
    } catch (error) {
      console.error("Error clearing report params from localStorage:", error);
    }
  };

  const getFilteredByValue = () => {
    return reportData && reportData.reportType
      ? Array.isArray(reportData.reportType)
        ? reportData.reportType
            .map((type) => type.charAt(0).toUpperCase() + type.slice(1))
            .join(", ")
        : reportData.reportType.charAt(0).toUpperCase() +
          reportData.reportType.slice(1)
      : "None";
  };

  return (
    <>
      <Box sx={{ p: "0px" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
            <Button onClick={clearError}>×</Button>
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                p: 1,
                border: "1px solid #95A0B3",
                borderRadius: "16px",
                boxShadow: "-2px 2px 8px 0px #0C305A40",
                ml: 2,
                padding: "24px",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#B7BFCC",
                  p: { xs: "12px 16px", sm: "16px 24px" },
                  borderRadius: "8px",
                  display: "flex",
                  gap: { xs: 1, sm: 2 },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: { xs: 2, sm: 3 },
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    ml: { xs: 0, sm: 3 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    Sites
                  </Typography>
                  <FormControl sx={{ width: { xs: "100%", sm: "250px" } }}>
                    {/* <InputLabel
                  sx={{ backgroundColor: "#fff", mt: "-4px" }}
                  shrink={allSites.length === 1 || selectedSiteId ? false : undefined}
                >
                  {allSites.length === 1 ? allSites[0].name || allSites[0].site_name : "Select a Site"}
                </InputLabel> */}
                    {allSites.length === 1 ? (
                      <div
                        style={{
                          background: "#FFFFFF",
                          borderRadius: "12px",
                          height: "46px",
                          border: "1px solid #183059",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 12px",
                        }}
                      >
                        {allSites[0].name || allSites[0].site_name}
                      </div>
                    ) : (
                      <Select
                        value={
                          selectedSiteId ||
                          (allSites.length > 0 ? allSites[0].id : "")
                        }
                        onChange={handleSiteChange}
                        sx={{
                          "& fieldset": {
                            border: "none",
                          },
                          background: "#FFFFFF",
                          borderRadius: "12px",
                          height: "46px",
                          border: "1px solid #183059",
                        }}
                      >
                        <MenuItem value="">
                          <em>All Sites</em>
                        </MenuItem>
                        {allSites.map((site) => (
                          <MenuItem key={site.id} value={site.id}>
                            {site.name || site.site_name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    mr: { xs: 0, sm: 3 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    Duration
                  </Typography>
                  <DateRangeSelector
                    dateOptions={dateOptions}
                    onChange={setDateOptions}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  />
                </Box>
              </Box>
              <CategorySelector
                reportType={reportType}
                onCategoryChange={setSelectedCategories}
                initialCategories={selectedCategories}
                vehicles={vehicles}
                routes={routes}
                drivers={drivers}
                stops={stops}
                users={users}
              />
              <Box sx={{ mt: 3, display: "flex", justifyContent: "left" }}>
                <Button
                  variant="contained"
                  onClick={handleGenerateReport}
                  disabled={loading || !isAnyCategorySelected} // <-- updated line
                  sx={{
                    border: "1px solid #414958",
                    backgroundColor: "#FFE600",
                    color: "black",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "#FFD700" },
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    borderRadius: "8px",
                    fontSize: "18px",
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : "Generate Report"}
                </Button>
              </Box>
            </Box>
            {/* <Modal
            open={openModal}
            onClose={() => {
              setOpenModal(false);
            }}
          > */}
            {/* Tabs Section */}
            {reportData && (
              <Box
                sx={{
                  flex: 1,
                  p: 1,
                  border: "1px solid #95A0B3",
                  borderRadius: "16px",
                  boxShadow: "-2px 2px 8px 0px #0C305A40",
                  ml: 2,
                  mt: 3,
                  padding: "24px",
                }}
              >
                <Box
                  sx={{
                    mt: 2,
                    bgcolor: "#F2F2F3",
                    border: "1px dashed #183059",
                    borderRadius: "8px",
                    // position: "absolute",
                    // top: "50%",
                    // left: "50%",
                    // transform: "translate(-50%, -50%)",
                    // width: { xs: "95%", sm: "85%", md: "70%" },
                    // maxHeight: { xs: "95vh", sm: "90vh" },
                    // bgcolor: "background.paper",
                    // boxShadow: 24,
                    // borderRadius: 2,
                    // overflow: "hidden",
                    // padding: { xs: "15px", sm: "20px", md: "30px" },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#F2F2F3",
                      border: "1px dashed #183059",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#e8eaf6",
                        p: { xs: 1, sm: 1.5, md: 2 },
                        borderBottom: "1px solid #ddd",
                        position: "relative",
                        borderRadius: "8px 8px 0px 0px",
                      }}
                    >
                      {/* <Box
                    sx={{
                      position: "absolute",
                      top: { xs: 4, sm: 6, md: 8 },
                      right: { xs: 4, sm: 6, md: 8 },
                      width: { xs: 20, sm: 22, md: 24 },
                      height: { xs: 20, sm: 22, md: 24 },
                      backgroundColor: "#EE3B2B",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "white",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      fontWeight: "bold",
                    }}
                    onClick={() => {
                      setOpenModal(false);
                      clearReportParamsFromStorage();
                    }}
                  >
                    ×
                  </Box> */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 600,
                          fontSize: { xs: "18px", sm: "24px", md: "30px" },
                          mb: 1,
                          textAlign: "center",
                          color: "#183059",
                          pr: { xs: 3, sm: 4, md: 0 },
                        }}
                      >
                        {getReportName(reportType)} Report Preview
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: "10px", sm: "15px", md: "20px" },
                          fontSize: "12px",
                          justifyContent: {
                            xs: "flex-start",
                            sm: "space-between",
                          },
                          mt: { xs: 1, sm: 1.5, md: 2 },
                        }}
                      >
                        {/* Date Range */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Duration
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {getDateRange(dateOptions.duration)}
                          </Typography>
                        </Box>

                        {/* Filter By */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Filtered by
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {/* {reportData && reportData.reportType
                          ? Array.isArray(reportData.reportType)
                            ? reportData.reportType
                              .map(
                                (type) =>
                                  type.charAt(0).toUpperCase() + type.slice(1)
                              )
                              .join(", ")
                            : reportData.reportType.charAt(0).toUpperCase() +
                            reportData.reportType.slice(1)
                          : "None"} */}
                            {getFilteredByValue()}
                          </Typography>
                        </Box>

                        {/* Speed Limit - Only for Overspeed */}
                        {reportType === "overspeed" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Speed Limit
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {settings?.maxSpeed} km/h
                            </Typography>
                          </Box>
                        )}

                        {reportType === "halt" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Inactive Time
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {
                                transformedReportData?.summary
                                  ?.totalInactiveTime?.string
                              }
                            </Typography>
                          </Box>
                        )}
                        {reportType === "halt" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Dead Time
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {
                                transformedReportData?.summary?.totalDeadTime
                                  ?.string
                              }
                            </Typography>
                          </Box>
                        )}

                        {/* Total Records */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Records
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {
                            // Object.entries(selectedCategories)
                            //   .filter(
                            //     ([_, data]) =>
                            //       data.selectedItems.length > 0 ||
                            //       data.isAllSelected
                            //   )
                            //   .map(([key, data]) =>
                            //     data.isAllSelected
                            //       ? `All ${
                            //           key.charAt(0).toUpperCase() + key.slice(1)
                            //         }`
                            //       : data.selectedItems.length <= 2
                            //       ? data.selectedItems.join(", ")
                            //       : `${data.selectedItems.length} ${
                            //           key.charAt(0).toUpperCase() + key.slice(1)
                            //         }`
                            //   )
                            //   .join(", ") ||
                              (reportData && reportData.data
                                ? `${reportData.data.length}`
                                : "0")
                                }
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#F2F2F3",
                        borderRadius: "0px 0px 8px 8px",
                        minHeight: { xs: "150px", sm: "175px", md: "200px" },
                        overflow: "auto",
                        "&::-webkit-scrollbar": {
                          width: { xs: "6px", sm: "8px", md: "10px" },
                          height: { xs: "6px", sm: "8px", md: "10px" },
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#647490",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ReportTable reportData={transformedReportData} />
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#647490",
                        p: { xs: 0.25, sm: 0.5, md: 1 },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: { xs: "16px", sm: "24px", md: "32px" },
                        height: { xs: "auto", sm: "50px", md: "80px" },
                        borderRadius: "12px",
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <ExportOptions
                        reportData={transformedReportData}
                        exportFormat={exportFormat}
                        setExportFormat={setExportFormat}
                        reportType={reportType}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 0.5, sm: 0.75, md: 1 },
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleEmailClick}
                          sx={{
                            width: { xs: "50%", sm: "90px", md: "90px" },
                            height: { xs: "36px", sm: "42px", md: "40px" },
                            gap: { xs: "4px", sm: "6px", md: "8px" },
                            borderRadius: "8px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#183059",
                            paddingTop: { xs: "8px", sm: "10px", md: "12px" },
                            paddingRight: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                            },
                            paddingBottom: {
                              xs: "8px",
                              sm: "10px",
                              md: "12px",
                            },
                            paddingLeft: { xs: "12px", sm: "14px", md: "16px" },
                            backgroundColor: "#20A8E0",
                            color: "white",
                            fontSize: { xs: "8px", sm: "9px", md: "14px" },
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#00acc1" },
                          }}
                        >
                          <img
                            src={Email}
                            alt="mail"
                            style={{
                              marginRight: "2px",
                              width: window.innerWidth < 600 ? "12px" : "20px",
                              height: window.innerWidth < 600 ? "12px" : "20px",
                            }}
                          />
                          Mail
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePrint}
                          sx={{
                            width: { xs: "50%", sm: "90px", md: "90px" },
                            height: { xs: "36px", sm: "42px", md: "40px" },
                            gap: { xs: "4px", sm: "6px", md: "8px" },
                            borderRadius: "8px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#183059",
                            paddingTop: { xs: "6px", sm: "8px", md: "10px" },
                            paddingRight: {
                              xs: "10px",
                              sm: "12px",
                              md: "14px",
                            },
                            paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                            paddingLeft: { xs: "10px", sm: "12px", md: "14px" },
                            backgroundColor: "#20A8E0",
                            color: "white",
                            fontSize: { xs: "8px", sm: "9px", md: "14px" },
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#00acc1" },
                          }}
                        >
                          <img
                            src={Print}
                            alt="print"
                            style={{
                              marginRight: "4px",
                              width: window.innerWidth < 600 ? "12px" : "20px",
                              height: window.innerWidth < 600 ? "12px" : "20px",
                            }}
                          />
                          Print
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* </Modal> */}
            <EmailReportModal
              open={emailModalOpen}
              onClose={() => setEmailModalOpen(false)}
              organizationUsers={users}
              reportType={getReportName(reportType)}
              exportFormat={exportFormat}
              reportCategory={reportType}
              dateOptions={dateOptions}
              selectedCategories={selectedCategories}
              selectedSiteId={selectedSiteId}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default ReportGenerator;
